import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import { validateLeave, createLeave, cancelLeave } from '../../services/leaveService';

// Kart sohbet balonlarıyla aynı satırda (MessageRow) duruyor; genişlik ve
// taşma kuralları MessageBubble ile aynı olmalı, aksi halde uzun bir izin
// türü veya onaylayıcı adı kartı kardeşlerinden geniş yapar.
const Card = styled.div`
  max-width: 85%;
  word-break: break-word;
  border: 1px solid ${(props) => props.theme.colors.primary.main};
  border-radius: ${(props) => props.theme.metrics.radius.medium};
  padding: 12px 14px;
  margin: 8px 0;
  background: ${(props) => props.theme.colors.bgColor};
  font-family: ${(props) => props.theme.metrics.font.family};
`;

const Baslik = styled.div`
  font-weight: ${(props) => props.theme.metrics.font.weight.bold};
  color: ${(props) => props.theme.colors.text.primary};
  font-size: ${(props) => props.theme.metrics.font.size.md};
  margin-bottom: 8px;
`;

const Satir = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: ${(props) => props.theme.metrics.font.size.sm};
  color: ${(props) => props.theme.colors.text.primary};
  padding: 2px 0;
`;

const Etiket = styled.span`
  opacity: 0.75;
`;

const Aksiyonlar = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const Sonuc = styled.div`
  margin-top: 10px;
  font-size: ${(props) => props.theme.metrics.font.size.sm};
  color: ${(props) => props.theme.colors.text.primary};
`;

// Backend'in gönderdiği "yyyy-MM-dd" YEREL tarihi (bkz. AiLeaveFunctions — kullanıcının
// saat dilimine göre zaten çözülmüş) aktif i18n diline göre biçimlendirir. new Date('yyyy-MM-dd')
// KASITLI OLARAK kullanılmıyor: bu, tarayıcının saat dilimine göre UTC gece yarısı üretir ve
// UTC'nin GERİSİNDEKİ saat dilimlerinde bir gün geri kayar. Bileşenler elle ayrıştırılıp
// yerel (tarayıcı saat dilimine göre ama gün/ay/yıl'ı asla değişmeyen) bir Date kurulur.
const izinTarihiFormatla = (isoTarih, dil) => {
  if (!isoTarih) return '';
  const parcalar = isoTarih.split('-').map(Number);
  if (parcalar.length !== 3 || parcalar.some(Number.isNaN)) return isoTarih;
  const [yil, ay, gun] = parcalar;
  const tarih = new Date(yil, ay - 1, gun);
  try {
    return new Intl.DateTimeFormat(dil, {
      day: 'numeric', month: 'long', year: 'numeric', weekday: 'long',
    }).format(tarih);
  } catch {
    return isoTarih;
  }
};

/**
 * AI asistanın hazırladığı izin işlemini onaylatan kart.
 * Yazma işlemi burada yapılır — LLM hiçbir kayıt oluşturmaz.
 * Onayda önce taze validate koşar: kart geçmişte kalmış olsa bile
 * bakiye/çakışma yeniden kontrol edilir.
 */
const LeaveConfirmCard = ({ payload, onDone }) => {
  const { t, i18n } = useTranslation();
  const [durum, setDurum] = useState('idle'); // idle | busy | done | canceled
  const [sonucMetni, setSonucMetni] = useState('');

  const izinOlusturma = payload.action === 'createLeave';

  const onayla = async () => {
    setDurum('busy');
    try {
      if (izinOlusturma) {
        const dogrulama = await validateLeave({
          leaveVariationId: payload.leaveVariationId,
          leaveStart: payload.leaveStart,
          leaveEnd: payload.leaveEnd,
          isHourly: false,
          afternoonExit: false,
          afternoonEntry: false,
          tz: payload.tz,
        });

        const d = dogrulama.data?.data ?? dogrulama.data;

        // Axios interceptor 401'de reddetmek yerine boş bir zarf ({status:false,
        // data:null}) döner (bkz. services/api.js) — reddetmediği için try/catch bunu
        // yakalamaz. Zarf boşsa d.leaveVariationId de olmaz; korumasız devam edilirse
        // createLeave'e tanımsız (undefined) alanlarla bir gövde giderdi.
        if (!d?.leaveVariationId) {
          setDurum('idle');
          return;
        }

        await createLeave({
          leaveVariationId: d.leaveVariationId,
          leaveStart: d.leaveStart,
          leaveEnd: d.leaveEnd,
          isHourly: d.isHourly,
          tz: d.tz,
          cost: d.cost,
          isPaid: d.isPaid,
          currentApprover: d.currentApprover,
          currentApproverType: d.currentApproverType,
          userNotes: payload.userNotes,
          createdVia: 'ai_assistant',
        });

        setSonucMetni(t('aiChat.leaveCreated'));
      } else {
        await cancelLeave(payload.leaveId);
        setSonucMetni(t('aiChat.leaveCanceled'));
      }
      setDurum('done');
      if (onDone) onDone();
    } catch {
      // Hata mesajı Axios interceptor'da gösterilir; kart tekrar denenebilir kalır.
      setDurum('idle');
    }
  };

  const vazgec = () => {
    setDurum('canceled');
    setSonucMetni(t('aiChat.confirmDismissed'));
  };

  return (
    <Card>
      <Baslik>
        {izinOlusturma ? t('aiChat.confirmLeaveTitle') : t('aiChat.confirmCancelTitle')}
      </Baslik>

      <Satir>
        <Etiket>{t('aiChat.leaveType')}</Etiket>
        <span>{payload.leaveTypeName}</span>
      </Satir>
      <Satir>
        <Etiket>{t('aiChat.leaveStart')}</Etiket>
        <span>{izinTarihiFormatla(payload.startDate, i18n.language)}</span>
      </Satir>
      {payload.lastLeaveDate && (
        <Satir>
          <Etiket>{t('aiChat.leaveLastDay')}</Etiket>
          <span>{izinTarihiFormatla(payload.lastLeaveDate, i18n.language)}</span>
        </Satir>
      )}
      <Satir>
        <Etiket>{t('aiChat.leaveReturn')}</Etiket>
        <span>{izinTarihiFormatla(payload.returnDate, i18n.language)}</span>
      </Satir>
      <Satir>
        <Etiket>{t('aiChat.leaveDays')}</Etiket>
        <span>{payload.cost}</span>
      </Satir>
      {payload.approverName && (
        <Satir>
          <Etiket>{t('aiChat.leaveApprover')}</Etiket>
          <span>{payload.approverName}</span>
        </Satir>
      )}
      {payload.hasBalanceCap && typeof payload.remainingBalanceBeforeRequest === 'number' && (
        <Satir>
          <Etiket>{t('aiChat.leaveRemaining')}</Etiket>
          <span>{payload.remainingBalanceBeforeRequest}</span>
        </Satir>
      )}
      {payload.startShifted && (
        <Satir>
          <Etiket>{t('aiChat.leaveStartShifted')}</Etiket>
          <span />
        </Satir>
      )}
      {payload.userNotes && (
        <Satir>
          <Etiket>{t('aiChat.leaveUserNotes')}</Etiket>
          <span>{payload.userNotes}</span>
        </Satir>
      )}

      {durum === 'idle' && (
        <Aksiyonlar>
          <Button onClick={onayla}>{t('aiChat.confirmApprove')}</Button>
          <Button variant="secondary" onClick={vazgec}>
            {t('aiChat.confirmDismiss')}
          </Button>
        </Aksiyonlar>
      )}
      {durum === 'busy' && <Sonuc>{t('aiChat.confirmProcessing')}</Sonuc>}
      {(durum === 'done' || durum === 'canceled') && <Sonuc>{sonucMetni}</Sonuc>}
    </Card>
  );
};

export default LeaveConfirmCard;
